import type { Order } from '@/services/orders';

interface TerminalBannerProps {
  readonly order: Order;
}

const TERMINAL_CONFIG: Record<string, { label: string; icon: 'cancel' | 'alert' | 'refund' }> = {
  cancelled: { label: 'Order cancelled', icon: 'cancel' },
  rejected: { label: 'Order rejected', icon: 'cancel' },
  failed: { label: 'Print failed', icon: 'alert' },
  refunded: { label: 'Order refunded', icon: 'refund' },
};

export const TerminalBanner = ({ order }: TerminalBannerProps) => {
  const config = TERMINAL_CONFIG[order.status];
  if (!config) return null;

  const reason = order.status === 'failed'
    ? order.failureReason
    : order.cancellationReason;

  const isRefunded = order.status === 'refunded';

  return (
    <div
      style={{
        padding: '10px 16px',
        borderRadius: 'var(--radius-md)',
        margin: '12px 0',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: isRefunded ? 'var(--bg-surface-sunken)' : 'var(--bg-error-subtle)',
        color: isRefunded ? 'var(--text-secondary)' : 'var(--admin-alert-text)',
        border: isRefunded
          ? '0.5px solid var(--border-default)'
          : '0.5px solid var(--admin-alert-border)',
      }}
    >
      <TerminalIcon type={config.icon} />
      <span>
        <strong>{config.label}</strong>
        {reason && <>: {reason}</>}
      </span>
    </div>
  );
};

const TerminalIcon = ({ type }: { readonly type: 'cancel' | 'alert' | 'refund' }) => {
  if (type === 'cancel') {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M5 5l6 6M11 5l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === 'alert') {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
        <path d="M8 1L15 14H1L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M8 6v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="8" cy="12" r="0.5" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <path d="M1 8a7 7 0 1014 0A7 7 0 001 8z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};
